import React, { useState } from 'react';
import { Tag, Plus, X, Edit3, Save, AlertCircle } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import TagSelector from './TagSelector';
import apiService from '../../services/api';

const TagManager = ({ contact, onContactUpdate, onError, onSuccess }) => {
  // Tema é usado apenas via classes dark: do Tailwind
  useTheme(); // Hook necessário para acesso ao tema
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTags, setSelectedTags] = useState(contact.tags || []);
  const [saving, setSaving] = useState(false);

  const handleSaveTags = async () => {
    try {
      setSaving(true);

      // Determinar quais tags adicionar e remover
      const currentTags = contact.tags || [];
      const tagsToAdd = selectedTags.filter(tag => !currentTags.includes(tag));
      const tagsToRemove = currentTags.filter(tag => !selectedTags.includes(tag));

      // Adicionar novas tags
      if (tagsToAdd.length > 0) {
        const addResponse = await apiService.request(`/crm/contacts/${contact.id}/tags`, {
          method: 'POST',
          body: JSON.stringify({ tags: tagsToAdd })
        });

        if (!addResponse.success) {
          throw new Error(addResponse.message || 'Erro ao adicionar tags');
        }
      }

      // Remover tags
      for (const tag of tagsToRemove) {
        const removeResponse = await apiService.request(`/crm/contacts/${contact.id}/tags/${encodeURIComponent(tag)}`, {
          method: 'DELETE'
        });
        
        if (!removeResponse.success) {
        }
      }

      // Atualizar contato local
      const updatedContact = {
        ...contact,
        tags: selectedTags
      };

      onContactUpdate(updatedContact);
      // Notificação removida
      setIsEditing(false);

    } catch (error) {
      onError('Erro ao salvar tags: ' + error.message);
      // Reverter mudanças locais
      setSelectedTags(contact.tags || []);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setSelectedTags(contact.tags || []);
    setIsEditing(false);
  };

  const handleQuickAddTag = async (tag) => {
    try {
      const response = await apiService.request(`/crm/contacts/${contact.id}/tags`, {
        method: 'POST',
        body: JSON.stringify({ tags: [tag] })
      });

      if (response.success && response.data.contact) {
        onContactUpdate(response.data.contact);
        // Notificação removida
      } else {
        throw new Error(response.message || 'Erro ao adicionar tag');
      }
    } catch (error) {
      onError('Erro ao adicionar tag: ' + error.message);
    }
  };

  const handleQuickRemoveTag = async (tag) => {
    try {
      const response = await apiService.request(`/crm/contacts/${contact.id}/tags/${encodeURIComponent(tag)}`, {
        method: 'DELETE'
      });

      if (response.success && response.data.contact) {
        onContactUpdate(response.data.contact);
        // Notificação removida
      } else {
        throw new Error(response.message || 'Erro ao remover tag');
      }
    } catch (error) {
      onError('Erro ao remover tag: ' + error.message);
    }
  };

  const currentTags = contact.tags || [];

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
          <Tag className="w-4 h-4 mr-2" />
          Tags do Contato
        </h4>
        
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Modo de visualização */}
      {!isEditing && (
        <div>
          {currentTags.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400 italic">
              Nenhuma tag atribuída
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {currentTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 group"
                >
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                  <button
                    onClick={() => handleQuickRemoveTag(tag)}
                    className="ml-1 opacity-0 group-hover:opacity-100 hover:text-blue-600 dark:hover:text-blue-300 transition-opacity"
                    title="Remover tag"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modo de edição */}
      {isEditing && (
        <div className="space-y-3">
          <TagSelector
            selectedTags={selectedTags}
            onTagsChange={setSelectedTags}
            placeholder="Selecionar tags para este contato..."
            allowCreate={true}
          />

          {/* Botões de ação */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleSaveTags}
              disabled={saving}
              className="flex items-center px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded text-sm transition-colors"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-3 h-3 mr-2" />
                  Salvar
                </>
              )}
            </button>
            
            <button
              onClick={handleCancel}
              disabled={saving}
              className="flex items-center px-3 py-1 bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400 text-white rounded text-sm transition-colors"
            >
              <X className="w-3 h-3 mr-2" />
              Cancelar
            </button>
          </div>

          {/* Informação sobre mudanças */}
          {JSON.stringify(selectedTags.sort()) !== JSON.stringify(currentTags.sort()) && (
            <div className="flex items-start space-x-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm">
              <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="text-yellow-800 dark:text-yellow-200">
                <div className="font-medium mb-1">Mudanças pendentes:</div>
                <div className="space-y-1 text-xs">
                  {selectedTags.filter(tag => !currentTags.includes(tag)).length > 0 && (
                    <div>
                      <span className="font-medium">Adicionar:</span> {selectedTags.filter(tag => !currentTags.includes(tag)).join(', ')}
                    </div>
                  )}
                  {currentTags.filter(tag => !selectedTags.includes(tag)).length > 0 && (
                    <div>
                      <span className="font-medium">Remover:</span> {currentTags.filter(tag => !selectedTags.includes(tag)).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tags sugeridas (apenas no modo visualização) */}
      {!isEditing && currentTags.length < 3 && (
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
          <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Tags sugeridas:</div>
          <div className="flex flex-wrap gap-1">
            {['interessado', 'cliente', 'lead', 'vip'].filter(tag => !currentTags.includes(tag)).slice(0, 3).map((tag) => (
              <button
                key={tag}
                onClick={() => handleQuickAddTag(tag)}
                className="inline-flex items-center px-2 py-1 rounded-full text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <Plus className="w-3 h-3 mr-1" />
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TagManager;